import { useState, FormEvent } from 'react';
import { apiService } from '../services/api';
import { OrganizationUser } from '../types/api.types';

interface UserFormProps {
  user?: OrganizationUser;
  organizationId?: number;
  onSuccess: () => void;
  onCancel: () => void;
}

// Generate a random password with letters, numbers, and symbols
function generatePassword(length = 16): string {
  const lowercase = 'abcdefghijklmnopqrstuvwxyz';
  const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const numbers = '0123456789';
  const symbols = '!@#$%^&*';
  const allChars = lowercase + uppercase + numbers + symbols;

  // Ensure at least one of each type
  let password = '';
  password += lowercase[Math.floor(Math.random() * lowercase.length)];
  password += uppercase[Math.floor(Math.random() * uppercase.length)];
  password += numbers[Math.floor(Math.random() * numbers.length)];
  password += symbols[Math.floor(Math.random() * symbols.length)];

  // Fill the rest randomly
  for (let i = password.length; i < length; i++) {
    password += allChars[Math.floor(Math.random() * allChars.length)];
  }

  // Shuffle the password
  return password.split('').sort(() => Math.random() - 0.5).join('');
}

export default function UserForm({ user, organizationId, onSuccess, onCancel }: UserFormProps) {
  const isEditing = !!user;

  const [username, setUsername] = useState(user?.username || '');
  const [email, setEmail] = useState(user?.email || '');
  const [fullName, setFullName] = useState(user?.full_name || '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  // Organization role: 'admin' or 'member' (displayed as User/Admin toggle)
  const [isAdmin, setIsAdmin] = useState(user?.organization_role?.toLowerCase() === 'admin');
  const [isActive, setIsActive] = useState(user?.is_active ?? true);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Edit mode: optional password reset
  const [wantsPasswordReset, setWantsPasswordReset] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);

  // Edit mode: MFA reset
  const [isResettingMFA, setIsResettingMFA] = useState(false);

  const handleGeneratePassword = () => {
    const generatedPwd = generatePassword(16);
    setPassword(generatedPwd);
    setShowPassword(true); // Show password so user can see/copy it
  };

  const handleGenerateNewPassword = () => {
    const generatedPwd = generatePassword(16);
    setNewPassword(generatedPwd);
    setShowNewPassword(true);
  };

  const handleResetMFA = async () => {
    if (!user) return;

    setIsResettingMFA(true);
    setError('');
    setSuccessMessage('');

    try {
      await apiService.clearUserMFA(user.id);
      setSuccessMessage('MFA has been cleared. User will need to set up MFA on next login.');
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      setError(apiService.getErrorMessage(err));
    } finally {
      setIsResettingMFA(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    // Validation
    if (!username.trim()) {
      setError('Username is required');
      return;
    }

    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    if (!isEditing && !password) {
      setError('Password is required for new users');
      return;
    }

    if (!isEditing && password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    // Validate new password if user wants to reset it
    if (isEditing && wantsPasswordReset && newPassword.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }

    setIsLoading(true);

    try {
      // Organization role: 'admin' or 'member'
      const orgRole = isAdmin ? 'admin' : 'member';

      if (isEditing) {
        await apiService.updateUser(user.id, {
          email: email.trim(),
          full_name: fullName.trim() || undefined,
          is_active: isActive,
        });
        // Update organization role if editing within an org context
        if (organizationId) {
          await apiService.changeUserRoleInOrganization(organizationId, user.id, orgRole);
        }
        // Reset password if requested
        if (wantsPasswordReset && newPassword) {
          await apiService.resetUserPassword(user.id, newPassword);
        }
      } else {
        // Create user with system role 'user' (always)
        const newUser = await apiService.createUser({
          username: username.trim(),
          email: email.trim(),
          full_name: fullName.trim() || undefined,
          password,
          role: 'user',
          is_active: isActive,
        });

        // Add the new user to the organization with the selected org role
        if (organizationId && newUser.user_id) {
          await apiService.addUserToOrganization(organizationId, newUser.user_id, orgRole);
        }
      }
      onSuccess();
    } catch (err) {
      setError(apiService.getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900">
            {isEditing ? 'Edit User' : 'Add New User'}
          </h3>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600"
            title="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
              {error}
            </div>
          )}

          {successMessage && (
            <div className="p-3 bg-green-50 border border-green-200 rounded text-green-700 text-sm">
              {successMessage}
            </div>
          )}

          <div>
            <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
              Username <span className="text-red-500">*</span>
            </label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className={`w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                isEditing ? 'bg-gray-100 text-gray-500' : ''
              }`}
              disabled={isLoading || isEditing}
              autoFocus={!isEditing}
            />
            {isEditing && (
              <p className="mt-1 text-xs text-gray-500">Username cannot be changed</p>
            )}
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={isLoading}
            />
          </div>

          <div>
            <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">
              Full Name
            </label>
            <input
              id="fullName"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={isLoading}
            />
          </div>

          {!isEditing && (
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    autoComplete="new-password"
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={handleGeneratePassword}
                  className="btn-app btn-app--neutral whitespace-nowrap"
                  disabled={isLoading}
                >
                  Generate
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500">Must be at least 8 characters</p>
            </div>
          )}

          {/* Role Toggle and Status Checkbox - Same Row */}
          <div className="grid grid-cols-2 gap-4">
            {/* Role Toggle */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
              <div className="flex items-center gap-2">
                <span className={`text-sm ${!isAdmin ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                  User
                </span>
                <button
                  type="button"
                  onClick={() => setIsAdmin(!isAdmin)}
                  disabled={isLoading}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    isAdmin ? 'bg-purple-600' : 'bg-gray-300'
                  } disabled:opacity-50`}
                  role="switch"
                  aria-checked={isAdmin}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      isAdmin ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
                <span className={`text-sm ${isAdmin ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                  Admin
                </span>
              </div>
            </div>

            {/* Status Checkbox */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <div className="flex items-center">
                <input
                  id="isActive"
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  disabled={isLoading}
                />
                <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700">
                  Active
                </label>
              </div>
            </div>
          </div>

          {/* Password Reset Section - Edit Mode Only */}
          {isEditing && (
            <div className="border-t border-gray-200 pt-4">
              <div className="flex items-center mb-2">
                <input
                  id="wantsPasswordReset"
                  type="checkbox"
                  checked={wantsPasswordReset}
                  onChange={(e) => {
                    setWantsPasswordReset(e.target.checked);
                    if (!e.target.checked) {
                      setNewPassword('');
                      setShowNewPassword(false);
                    }
                  }}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  disabled={isLoading}
                />
                <label htmlFor="wantsPasswordReset" className="ml-2 block text-sm font-medium text-gray-700">
                  Set New Password
                </label>
              </div>

              {wantsPasswordReset && (
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="New password (min 8 characters)"
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      autoComplete="new-password"
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      tabIndex={-1}
                    >
                      {showNewPassword ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={handleGenerateNewPassword}
                    className="btn-app btn-app--neutral whitespace-nowrap"
                    disabled={isLoading}
                  >
                    Generate
                  </button>
                </div>
              )}
            </div>
          )}

          {/* MFA Reset Section - Edit Mode Only */}
          {isEditing && (
            <div className="border-t border-gray-200 pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">MFA Status</label>
              <div className="flex items-center justify-between">
                <span className={`text-sm ${user?.mfa_enabled ? 'text-green-600' : 'text-gray-500'}`}>
                  {user?.mfa_enabled ? 'MFA is enabled' : 'MFA is not enabled'}
                </span>
                {user?.mfa_enabled && (
                  <button
                    type="button"
                    onClick={handleResetMFA}
                    disabled={isLoading || isResettingMFA}
                    className="btn-app btn-app--danger"
                  >
                    {isResettingMFA ? 'Clearing...' : 'Clear MFA'}
                  </button>
                )}
              </div>
              {user?.mfa_enabled && (
                <p className="mt-1 text-xs text-gray-500">
                  Clearing MFA will require the user to set up MFA again on their next login.
                </p>
              )}
            </div>
          )}

          <div className="flex gap-2 pt-4">
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              className="flex-1 btn-app btn-app--neutral"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 btn-app btn-app--primary"
            >
              {isLoading ? 'Saving...' : isEditing ? 'Save Changes' : 'Add New User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
